import React, { PureComponent, createRef, RefObject, ElementRef } from 'react';
import { StyleSheet, View, ScrollView } from 'react-native';

import FormField from '../helpers/FormField';
import { Checkbox } from '../../../../core-ui';
import Imm from '../../../../support/ImmutableHelpers';
import formatDate from '../../../../core/support/formatDate';

import LoadingIndicator from '../../../uiold/LoadingIndicator';
import {
  Checkbox as OldCheckbox,
  CollapsibleView,
  Text,
  DatePicker,
  Separator,
} from '../../../uiold';
import { themeColors } from '../../../uiold/Constants';

import IndexedList from '../../../../support/shared/IndexedList';
import { Market } from '../../../../types/shared/Market';
import { MarketDates } from '../../../../types/shared/Vendor';
import { MarketSelectorElementDefinition } from '../../../../types/shared/FormFieldDefinition';
import { fromValue } from './MarketSelectorEditView';

// key = market.id + '|' + date;
type MarketSelections = {
  [key: string]: { market: Market; date: string };
};

type CollapsibleViewInstance = ElementRef<typeof CollapsibleView>;

export type MarketSelectorFormViewProps = {
  definition: MarketSelectorElementDefinition;
  handleMarketDateSelect?: (value: MarketDates) => void;
  invoiceView?: boolean;
  markets?: IndexedList<Market> | Array<Market> | null | undefined;
  onChange?: (id: string, selections: MarketDates) => void;
  readOnly?: boolean;
  hideLabel?: boolean;
  value?: MarketDates;
};

type State = {
  selections: MarketSelections;
  collapsibleRefs: ObjectOf<RefObject<CollapsibleViewInstance>>;
};

class MarketSelectorFormView extends PureComponent<
  MarketSelectorFormViewProps,
  State
> {
  state: State = {
    selections:
      this.props.value && this.props.markets
        ? fromValue(this.props.value, this.props.markets)
        : {},
    collapsibleRefs: {},
  };

  static getDerivedStateFromProps(
    props: MarketSelectorFormViewProps,
    state: State,
  ) {
    if (props.markets == null) {
      return;
    }
    let { markets } = props;
    let collapsibleRefs: ObjectOf<RefObject<CollapsibleViewInstance>> = {};
    markets.forEach(({ id }) => {
      collapsibleRefs[id] = state.collapsibleRefs[id] || createRef();
    });
    return { collapsibleRefs };
  }

  render() {
    let { props } = this;
    return <FormField {...props}>{this._renderField()}</FormField>;
  }

  _renderField = () => {
    let { markets, invoiceView, definition } = this.props;
    if (!markets) {
      return <LoadingIndicator />;
    }
    let totalMarketDates = 0;
    let numMarkets = markets.length;
    if (
      definition.hiddenInForm &&
      !this.props.readOnly &&
      !this.props.invoiceView
    ) {
      return null;
    }
    let marketsArray = Array.isArray(markets) ? markets : markets.getList();
    let marketItems = marketsArray.map((market) => {
      totalMarketDates += market.dates.length;
      // Don't show a market if there are no dates to select.
      if (market.hidden || market.dates.length === 0) {
        return <span key={market.id} />;
      }
      // Collapse date selection if and only if there's more than 1 market or
      // the market dates exceed 40.
      let initiallyCollapsed = numMarkets > 1 || market.dates.length > 40;
      let anySelected = this._isAnyDateSelected(market);
      let allSelected = this._areAllDatesSelected(market);
      return (
        // Not collapsible if market dates < 4 and don't show select all dates if market dates <4
        <View style={styles.rowFlex} key={market.id}>
          {!definition.disableSelectAll && (
            <View style={styles.outsideCheckbox}>
              <Checkbox
                isIndeterminate={anySelected && !allSelected}
                isChecked={allSelected}
                onChange={() => {
                  if (!invoiceView) {
                    let ref = this.state.collapsibleRefs[market.id];
                    // eslint-disable-next-line no-underscore-dangle
                    ref && ref.current && ref.current._toCollapsed();
                  }
                  this._onChangeAll(market);
                }}
                paddingRight={2}
              />
            </View>
          )}

          <CollapsibleView
            ref={
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              this.state.collapsibleRefs[market.id]!
            }
            key={market.id}
            isDisabled={market.dates.length <= 0}
            noLeftCheckbox={true}
            title={market.name}
            style={styles.box}
            subtitle={market.description}
            initialCollapseState={market.dates.length > 0 && initiallyCollapsed}
          >
            {!definition.disableSelectAll &&
              this._renderMarketDatesSelectAll(market)}
            {!definition.calendarView ? (
              <View style={styles.marketDates}>
                {this._renderMarketDates(market)}
              </View>
            ) : (
              <View style={styles.marketDates}>
                {this._renderMarketDatesAsCalendar(market)}
              </View>
            )}
          </CollapsibleView>
        </View>
      );
    });
    if (totalMarketDates === 0) {
      return <Text type="label">{t('No market dates available.')}</Text>;
    }
    return <View>{marketItems}</View>;
  };

  _renderMarketDatesSelectAll = (market: Market) => {
    let { readOnly } = this.props;
    if (market.dates.length <= 3) {
      return;
    }
    return (
      <View style={styles.selectAll}>
        <OldCheckbox
          tabIndex={readOnly ? -1 : undefined}
          isChecked={this._areAllDatesSelected(market)}
          onChange={() => this._onChangeAll(market)}
          text={t('(Select All Dates)')}
        />
      </View>
    );
  };

  _renderMarketDates = (market: Market): React.ReactNode => {
    let { readOnly, definition } = this.props;
    return market.dates.map((date) => {
      return (
        <View
          style={[
            styles.marketDate,
            definition?.calendarView && styles.marketDateCalendar,
          ]}
          key={date}
        >
          <OldCheckbox
            tabIndex={readOnly ? -1 : undefined}
            isChecked={this._isDateSelected(market, date)}
            onChange={() => this._onChange(market, date)}
            text={formatDate(date, { fromUTC: true, includeDayOfWeek: true })}
          />
        </View>
      );
    });
  };

  _renderMarketDatesAsCalendar = (market: Market) => {
    let { readOnly } = this.props;
    let { selections } = this.state;
    let selectionValues = this._getValue(selections);
    let availableDates = new Set(market.dates);
    let selectedDates = new Set(selectionValues?.[market.id]);
    let onDatePickerClick = (market: Market, date: string) => {
      if (!availableDates.has(date)) {
        return;
      }
      this._onChange(market, date);
    };

    return (
      <>
        <View style={styles.datePickerContainer}>
          <DatePicker
            selectedDates={selectedDates}
            availableDates={availableDates}
            onDateClick={(date) => {
              !readOnly ? onDatePickerClick(market, date) : () => null;
            }}
          />
        </View>
        <View style={styles.selectedDateList}>
          <Text style={styles.datesText}>{t('Dates')}</Text>
          <Separator style={styles.datesSeparator} />
          <ScrollView style={styles.dateList}>
            {this._renderMarketDates(market)}
          </ScrollView>
        </View>
      </>
    );
  };

  _isDateSelected = (market: Market, date: string) => {
    let key = market.id + '|' + date;
    return this.state.selections[key] != null;
  };

  _onChange = (market: Market, date: string) => {
    let key = market.id + '|' + date;
    let { selections } = this.state;
    if (this._isDateSelected(market, date)) {
      selections = Imm.deleteProperty(selections, key);
    } else {
      selections = Imm.setProperty(selections, key, { market, date });
    }
    this._updateSelections(selections);
  };

  _isAnyDateSelected = (market: Market): boolean => {
    let { selections } = this.state;
    let result = false;
    if (!selections) {
      return result;
    }
    for (let date of market.dates) {
      let key = market.id + '|' + date;
      if (selections[key]) {
        return true;
      }
    }
    return result;
  };

  _areAllDatesSelected = (market: Market) => {
    let { selections } = this.state;
    for (let date of market.dates) {
      let key = market.id + '|' + date;
      if (selections[key] == null) {
        return false;
      }
    }
    return true;
  };

  _onChangeAll = (market: Market) => {
    if (this._areAllDatesSelected(market)) {
      this._selectAllDates(market, false);
    } else {
      this._selectAllDates(market, true);
    }
  };

  _selectAllDates = (market: Market, isSelect: boolean) => {
    let selections = { ...this.state.selections };
    for (let date of market.dates) {
      let key = market.id + '|' + date;
      if (isSelect) {
        selections[key] = { market, date };
      } else {
        delete selections[key];
      }
    }
    this._updateSelections(selections);
  };

  selectAll = () => {
    let { markets } = this.props;
    let selections: MarketSelections = {};
    let marketsArray = Array.isArray(markets)
      ? markets
      : markets?.getList() ?? [];
    for (let market of marketsArray) {
      for (let date of market.dates) {
        let key = market.id + '|' + date;
        selections[key] = { market, date };
      }
    }
    this._updateSelections(selections);
  };

  _updateSelections = (selections: MarketSelections) => {
    this.setState({ selections });
    let { onChange, definition, handleMarketDateSelect } = this.props;
    if (onChange != null) {
      onChange(definition.id, this._getValue(selections));
    }
    if (handleMarketDateSelect != null) {
      handleMarketDateSelect(this._getValue(selections));
    }
  };

  _getValue = (selections: MarketSelections) => {
    let { markets } = this.props;
    let byMarketID: ObjectOf<Array<string>> = {};
    if (!markets) {
      return byMarketID;
    }
    markets.forEach((market) => {
      let { dates, id } = market;
      let selectedMarketDates = dates.filter(
        (date) => selections[`${id}|${date}`] != null,
      );
      // Do not include the market if no date is selected.
      // The server side validator treats object with at least one key as not empty
      // even though the value is empty.
      if (selectedMarketDates.length) {
        byMarketID[id] = selectedMarketDates;
      }
    });
    return byMarketID;
  };

  getValue = () => {
    let { selections } = this.state;
    return this._getValue(selections);
  };
}

let styles = StyleSheet.create({
  marketDates: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  marketDate: {
    marginVertical: 7,
    paddingRight: 7,
    minWidth: '20%',
  },
  marketDateCalendar: {
    paddingLeft: 12,
  },
  selectAll: {
    flexDirection: 'row',
    marginVertical: 7,
  },
  box: {
    marginTop: 10,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: '#f4f4f4',
    width: '95%',
  },
  rowFlex: { flexDirection: 'row' },
  outsideCheckbox: {
    marginTop: 10,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  datePickerContainer: {
    flex: 2,
    padding: 10,
    minHeight: 380,
    marginRight: 20,
    borderRadius: 5,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
  },
  selectedDateList: {
    flex: 1,
    minHeight: 380,
    paddingTop: 10,
    borderRadius: 5,
    borderColor: themeColors.borderGrey,
    borderWidth: StyleSheet.hairlineWidth,
  },
  dateList: {
    height: 0,
  },
  datesText: {
    alignSelf: 'center',
  },
  datesSeparator: {
    backgroundColor: themeColors.blue,
    marginVertical: 10,
  },
});

export default MarketSelectorFormView;
